import articles from "../data/articles.json";
import {
    ENDPOINTS,
    normalizeArticlePaidValue,
    normalizeArticleSectionValue,
} from "./NewsQueueApiConstants";

const DEBUG = true;
const LOCK_TTL_MS = 30 * 1000;
const LOCAL_LOCK_OWNER = "local-user";
const newsLocks = new Map();

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function statusFromPublished(published) {
    if (published === true) return "published";
    if (published === false) return "rejected";
    return "new";
}

function defaultTargetMedia(raw) {
    return raw?.target_media ?? raw?.targetMedia ?? "HelsinginUutiset";
}

function toSources(raw) {
    const publishDate = raw?.timestamp ?? new Date().toISOString();
    const url = raw?.url ?? "";
    return [
        {
            url,
            url_name: raw?.header ?? "",
            publish_date: publishDate,
            type: raw?.source ?? "",
            description: raw?.ingress ?? null,
        },
    ];
}

function toApiNews(raw, index) {
    const id = raw?.id != null ? String(raw.id) : String(index);
    const timestamp = raw?.timestamp;
    const nowIso = new Date().toISOString();

    const publish_state = raw?.publish_state ?? statusFromPublished(raw?.published);

    return {
        id,
        source_type: raw?.source,
        target_media: defaultTargetMedia(raw),
        category: raw?.category ?? "Paikalliset",
        article_header: raw?.header,
        article_ingress: raw?.ingress,
        article_body: raw?.body,
        timestamp_article: timestamp,
        timestamp_created: raw?.timestamp_created ?? timestamp ?? nowIso,
        timestamp_updated: raw?.timestamp_updated ?? timestamp ?? nowIso,
        timestamp_approved: null,
        publish_state,
        article_author: raw?.article_author ?? "Arkistokanta",
        article_footer: raw?.article_footer ?? "Tämä juttu on tehty tekoälyllä.",
        article_background: raw?.article_background ?? null,
        article_paid: normalizeArticlePaidValue(raw?.article_paid),
        ai_clause: raw?.ai_clause ?? true,
        article_section: normalizeArticleSectionValue(raw?.article_section),
        sources: raw?.sources ?? toSources(raw),
        publish_time: raw?.publish_time ?? null,
        approver: raw?.approver ?? null,
        feedback: raw?.feedback ?? null,
    };
}

function toApiNewsCore(raw, index) {
    const full = toApiNews(raw, index);
    // Core response omits body
    // eslint-disable-next-line no-unused-vars
    const { article_body, ...core } = full;
    return core;
}

function getByIdOrFirst(idLike) {
    const id = idLike != null ? String(idLike) : null;
    if (id) {
        const idx = articles.findIndex((a, i) => String(a?.id ?? i) === id);
        if (idx >= 0) return toApiNews(articles[idx], idx);
        return null;
    }
    return articles.length > 0 ? toApiNews(articles[0], 0) : null;
}

function asIso(date) {
    return date instanceof Date ? date.toISOString() : null;
}

function isActiveLock(lock) {
    if (!lock?.lock_expires_at) return false;
    return new Date(lock.lock_expires_at).getTime() > Date.now();
}

function pruneExpiredLocks() {
    for (const [newsId, lock] of newsLocks.entries()) {
        if (!isActiveLock(lock)) {
            newsLocks.delete(newsId);
        }
    }
}

function upsertLock(newsId) {
    const expiresAt = new Date(Date.now() + LOCK_TTL_MS);
    const payload = {
        news_id: String(newsId),
        locked_by: LOCAL_LOCK_OWNER,
        lock_expires_at: asIso(expiresAt),
    };
    newsLocks.set(String(newsId), payload);
    return payload;
}

export async function localFetch({ endpoint, fetchUrl, fetchParameters, parameters }) {
    if (DEBUG) console.log(`localNewsQueueFetch :: ${fetchParameters.method} :: ${fetchUrl}`);

    await delay(250);

    switch (endpoint) {
        case ENDPOINTS.listNews: {
            const targetMedia = parameters?.target_media ?? parameters?.targetMedia;
            const targetMediaList =
                targetMedia == null ? null : Array.isArray(targetMedia) ? targetMedia : [targetMedia];
            const showProcessed = parameters?.show_processed ?? parameters?.showProcessed ?? false;
            const afterRaw = parameters?.after ?? parameters?.after_timestamp ?? parameters?.afterTimestamp;
            const afterTs = afterRaw ? Date.parse(String(afterRaw)) : NaN;

            let items = articles.map((a, i) => toApiNewsCore(a, i));

            if (!showProcessed) {
                items = items.filter((x) => x?.publish_state === "new");
            }

            if (targetMediaList && targetMediaList.length > 0) {
                const allowed = new Set(targetMediaList.map((x) => String(x)));
                items = items.filter((x) => allowed.has(String(x?.target_media)));
            }

            if (!Number.isNaN(afterTs)) {
                items = items.filter((x) => {
                    const createdTs = Date.parse(String(x?.timestamp_created ?? ""));
                    return !Number.isNaN(createdTs) && createdTs > afterTs;
                });
            }

            return new Response(JSON.stringify(items), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.getNews: {
            const newsId = parameters?.news_id ?? parameters?.newsId ?? parameters?.id;
            const item = getByIdOrFirst(newsId);
            return new Response(JSON.stringify(item), {
                status: item ? 200 : 404,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.addNews: {
            const nowIso = new Date().toISOString();

            const body = fetchParameters?.body ? JSON.parse(fetchParameters.body) : {};
            const response = {
                ...body,
                id: crypto?.randomUUID ? crypto.randomUUID() : "00000000-0000-0000-0000-000000000000",
                timestamp_created: nowIso,
                timestamp_updated: nowIso,
                timestamp_approved: null,
                publish_state: body?.publish_state ?? "new",
                target_media: body?.target_media ?? body?.targetMedia ?? "HelsinginUutiset",
                category: body?.category ?? "Paikalliset",
                article_author: body?.article_author ?? "Arkistokanta",
                article_footer: body?.article_footer ?? "Tämä juttu on tehty tekoälyllä.",
                sources: body?.sources ?? toSources(body),
            };
            return new Response(JSON.stringify(response), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.updateNews: {
            const nowIso = new Date().toISOString();
            const newsId = parameters?.news_id ?? parameters?.newsId ?? parameters?.id;
            const patch = fetchParameters?.body ? JSON.parse(fetchParameters.body) : {};
            const existing = getByIdOrFirst(newsId);

            const response = {
                ...(existing ?? {}),
                ...patch,
                id: String(newsId ?? existing?.id ?? "00000000-0000-0000-0000-000000000000"),
                timestamp_updated: nowIso,
                target_media:
                    patch?.target_media ?? patch?.targetMedia ?? existing?.target_media ?? "HelsinginUutiset",
                category: patch?.category ?? existing?.category ?? "Paikalliset",
                article_author: patch?.article_author ?? existing?.article_author ?? "Arkistokanta",
                article_footer:
                    patch?.article_footer ?? existing?.article_footer ?? "Tämä juttu on tehty tekoälyllä.",
                sources: patch?.sources ?? existing?.sources ?? toSources(existing ?? patch),
                timestamp_approved: patch?.timestamp_approved ?? existing?.timestamp_approved ?? null,
            };

            return new Response(JSON.stringify(response), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.patchNews: {
            const nowIso = new Date().toISOString();
            const newsId = parameters?.news_id ?? parameters?.newsId ?? parameters?.id;
            const patch = fetchParameters?.body ? JSON.parse(fetchParameters.body) : {};
            const existing = getByIdOrFirst(newsId);

            const response = {
                ...(existing ?? {}),
                id: String(newsId ?? existing?.id ?? "00000000-0000-0000-0000-000000000000"),
                publish_state: "rejected",
                feedback: patch?.feedback ?? existing?.feedback ?? null,
                timestamp_updated: nowIso,
            };

            return new Response(JSON.stringify(response), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.listNewsLocks: {
            pruneExpiredLocks();
            const response = Array.from(newsLocks.values());
            return new Response(JSON.stringify(response), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.acquireNewsLock: {
            pruneExpiredLocks();
            const newsId = parameters?.news_id ?? parameters?.newsId ?? parameters?.id;
            const existing = newsLocks.get(String(newsId));

            if (existing && existing.locked_by !== LOCAL_LOCK_OWNER && isActiveLock(existing)) {
                return new Response(
                    JSON.stringify({ detail: `News ${newsId} is locked by another user.` }),
                    {
                        status: 409,
                        headers: { "Content-Type": "application/json" },
                    }
                );
            }

            const response = upsertLock(newsId);

            return new Response(JSON.stringify(response), {
                status: 200,
                headers: { "Content-Type": "application/json" },
            });
        }

        case ENDPOINTS.releaseNewsLock: {
            const newsId = parameters?.news_id ?? parameters?.newsId ?? parameters?.id;
            newsLocks.delete(String(newsId));

            return new Response(null, {
                status: 204,
            });
        }

        case ENDPOINTS.getConfigurations: {
            const targetMedias = Array.from(
                new Set(articles.map((a) => defaultTargetMedia(a)).filter(Boolean).map((x) => String(x)))
            );
            const newsSources = Array.from(
                new Set(articles.map((a) => a?.source).filter(Boolean).map((x) => String(x)))
            );
            const newsCategories = Array.from(
                new Set(articles.map((a) => a?.category).filter(Boolean).map((x) => String(x)))
            );

            // Mock categories by group structure
            const categoriesByGroup = {
                "viranomaiset": ["poliisi", "pelastus"],
                "politiikka": ["päätökset"],
                "paikalliset": ["tapahtumat", "uutiset"]
            };

            return new Response(
                JSON.stringify({
                    target_medias: targetMedias,
                    news_sources: newsSources,
                    feedback_categories: [],
                    news_categories: newsCategories,
                    news_categories_by_group: categoriesByGroup,
                }),
                {
                    status: 200,
                    headers: { "Content-Type": "application/json" },
                }
            );
        }

        default:
            throw new Error(`localNewsQueueFetch :: Unknown endpoint: ${endpoint}`);
    }
}
